import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LeadSourceChart from './components/LeadSourceChart';
import ConversionFunnelChart from './components/ConversionFunnelChart';
import FollowupPerformanceChart from './components/FollowupPerformanceChart';
import PipelineForecastChart from './components/PipelineForecastChart';
import { prospectService } from '../../services/prospectService';

const TABS = [
  { id: 'lead-generation', label: 'Lead Generation', icon: 'Target' },
  { id: 'conversion', label: 'Conversion Metrics', icon: 'TrendingUp' },
  { id: 'followup', label: 'Follow-up Performance', icon: 'Calendar' },
  { id: 'forecast', label: 'Pipeline Forecast', icon: 'BarChart3' },
];

const MetricCard = ({ label, value, subValue, icon, color = 'text-accent', trend }) => (
  <div className="bg-card border border-border rounded-lg p-5">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
        {subValue && <p className="text-xs text-muted-foreground mt-1">{subValue}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center`}>
        <Icon name={icon} size={20} className="text-accent" />
      </div>
    </div>
    {trend != null && (
      <div className={`flex items-center gap-1 mt-3 text-xs font-medium ${
        trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-muted-foreground'
      }`}>
        <Icon name={trend > 0 ? 'TrendingUp' : trend < 0 ? 'TrendingDown' : 'Minus'} size={12} />
        {trend > 0 ? '+' : ''}{trend}% vs last month
      </div>
    )}
  </div>
);

const ProspectAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('lead-generation');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { loadAnalytics(); }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await prospectService?.getAnalytics();
    if (err) setError('Failed to load analytics data');
    else setAnalyticsData(data);
    setLoading(false);
  };

  const metrics = useMemo(() => {
    if (!analyticsData) return null;
    const { prospects = [], followups = [], interactions = [] } = analyticsData;

    const total = prospects?.length;
    const won = prospects?.filter(p => p?.status === 'won')?.length;
    const active = prospects?.filter(p => !['won', 'lost']?.includes(p?.status))?.length;
    const conversionRate = total > 0 ? ((won / total) * 100)?.toFixed(1) : 0;
    const totalPipelineValue = prospects?.filter(p => !['won', 'lost']?.includes(p?.status))?.reduce((sum, p) => sum + (Number(p?.estimatedValue) || 0), 0);
    const weightedValue = prospects?.filter(p => !['won', 'lost']?.includes(p?.status))?.reduce((sum, p) => sum + ((Number(p?.estimatedValue) || 0) * (Number(p?.probability) || 0) / 100), 0);
    const avgLeadScore = total > 0 ? Math.round(prospects?.reduce((sum, p) => sum + (Number(p?.leadScore) || 0), 0) / total) : 0;
    const overdueFollowups = followups?.filter(f => f?.followupStatus === 'overdue')?.length;
    const completedFollowups = followups?.filter(f => f?.followupStatus === 'completed')?.length;
    const scheduledFollowups = followups?.filter(f => f?.followupStatus === 'scheduled')?.length;
    const followupCompletionRate = (scheduledFollowups + completedFollowups) > 0
      ? ((completedFollowups / (scheduledFollowups + completedFollowups)) * 100)?.toFixed(0)
      : 0;

    return {
      total, won, active, conversionRate, totalPipelineValue, weightedValue,
      avgLeadScore, overdueFollowups, completedFollowups, scheduledFollowups,
      followupCompletionRate, totalInteractions: interactions?.length
    };
  }, [analyticsData]);

  const sourceChartData = useMemo(() => {
    if (!analyticsData?.prospects) return [];
    const counts = {};
    analyticsData?.prospects?.forEach(p => {
      const src = p?.source?.replace(/_/g, ' ') || 'other';
      counts[src] = (counts?.[src] || 0) + 1;
    });
    return Object.entries(counts)?.map(([name, value]) => ({ name: name?.charAt(0)?.toUpperCase() + name?.slice(1), value }));
  }, [analyticsData]);

  const funnelData = useMemo(() => {
    if (!analyticsData?.prospects) return [];
    const stages = ['new', 'contacted', 'qualified', 'proposal_sent', 'negotiating', 'won', 'lost'];
    return stages?.map(key => ({
      key,
      name: key?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
      count: analyticsData?.prospects?.filter(p => p?.status === key)?.length
    }))?.filter(s => s?.count > 0);
  }, [analyticsData]);

  const companyTypeData = useMemo(() => {
    if (!analyticsData?.prospects) return [];
    const counts = {};
    analyticsData?.prospects?.forEach(p => {
      const t = p?.companyType?.replace(/_/g, ' ') || 'other';
      counts[t] = (counts?.[t] || 0) + 1;
    });
    return Object.entries(counts)?.map(([name, value]) => ({
      name: name?.charAt(0)?.toUpperCase() + name?.slice(1), value
    }));
  }, [analyticsData]);

  const followupChartData = useMemo(() => {
    if (!analyticsData?.followups) return [];
    const typeGroups = {};
    analyticsData?.followups?.forEach(f => {
      const t = f?.interactionType?.replace(/_/g, ' ') || 'other';
      if (!typeGroups?.[t]) typeGroups[t] = { name: t?.charAt(0)?.toUpperCase() + t?.slice(1), scheduled: 0, completed: 0, overdue: 0 };
      if (f?.followupStatus === 'scheduled') typeGroups[t].scheduled++;
      else if (f?.followupStatus === 'completed') typeGroups[t].completed++;
      else if (f?.followupStatus === 'overdue') typeGroups[t].overdue++;
    });
    return Object.values(typeGroups);
  }, [analyticsData]);

  const forecastData = useMemo(() => {
    if (!analyticsData?.prospects) return [];
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = subMonths(new Date(), i);
      const start = startOfMonth(d);
      const end = endOfMonth(d);
      const monthProspects = analyticsData?.prospects?.filter(p => {
        const created = p?.createdAt ? new Date(p.createdAt) : null;
        return created && created >= start && created <= end;
      });
      const weightedValue = monthProspects?.reduce((sum, p) =>
        sum + ((Number(p?.estimatedValue) || 0) * (Number(p?.probability) || 0) / 100), 0);
      months?.push({ month: format(d, 'MMM yy'), value: Math.round(weightedValue) });
    }
    return months;
  }, [analyticsData]);

  const exportReport = () => {
    if (!analyticsData || !metrics) return;
    const rows = [
      ['Prospect Analytics Report', '', format(new Date(), 'MMM d, yyyy')],
      [],
      ['SUMMARY METRICS'],
      ['Total Prospects', metrics?.total],
      ['Active Prospects', metrics?.active],
      ['Won Prospects', metrics?.won],
      ['Conversion Rate', `${metrics?.conversionRate}%`],
      ['Total Pipeline Value', `$${metrics?.totalPipelineValue?.toLocaleString()}`],
      ['Weighted Pipeline Value', `$${Math.round(metrics?.weightedValue)?.toLocaleString()}`],
      ['Avg Lead Score', metrics?.avgLeadScore],
      ['Total Interactions', metrics?.totalInteractions],
      ['Follow-up Completion Rate', `${metrics?.followupCompletionRate}%`],
      [],
      ['PIPELINE BY STAGE'],
      ['Stage', 'Count'],
      ...funnelData?.map(s => [s?.name, s?.count]),
      [],
      ['LEADS BY SOURCE'],
      ['Source', 'Count'],
      ...sourceChartData?.map(s => [s?.name, s?.value]),
    ];
    const csv = rows?.map(r => r?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prospect-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/prospect-management')} className="text-muted-foreground hover:text-foreground">
              <Icon name="ArrowLeft" size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Prospect Analytics</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Sales pipeline insights and performance metrics</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={loadAnalytics} disabled={loading}>
              Refresh
            </Button>
            <Button variant="outline" iconName="Download" iconPosition="left" onClick={exportReport} disabled={!metrics}>
              Export CSV
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 flex items-center gap-2">
            <Icon name="AlertCircle" size={16} />
            {error}
            <Button size="sm" variant="outline" className="ml-auto" onClick={loadAnalytics}>Retry</Button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
          </div>
        ) : metrics ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Total Prospects" value={metrics?.total} icon="Target" subValue={`${metrics?.active} active`} trend={null} />
              <MetricCard label="Conversion Rate" value={`${metrics?.conversionRate}%`} icon="TrendingUp" subValue={`${metrics?.won} won`} color="text-green-600" trend={null} />
              <MetricCard label="Pipeline Value" value={`${(metrics?.totalPipelineValue / 1000)?.toFixed(0)}K`} icon="DollarSign" subValue={`${(metrics?.weightedValue / 1000)?.toFixed(0)}K weighted`} trend={null} />
              <MetricCard label="Avg Lead Score" value={metrics?.avgLeadScore} icon="Star" subValue={`${metrics?.overdueFollowups} overdue follow-ups`} color={metrics?.avgLeadScore >= 50 ? 'text-green-600' : 'text-amber-600'} trend={null} />
            </div>

            {/* Tabs */}
            <div className="border-b border-border">
              <div className="flex gap-1 overflow-x-auto">
                {TABS?.map(tab => (
                  <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                      activeTab === tab?.id ? 'border-accent text-accent' : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}>
                    <Icon name={tab?.icon} size={15} />
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
              {/* LEAD GENERATION */}
              {activeTab === 'lead-generation' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="PieChart" size={16} className="text-accent" />
                      Prospects by Source
                    </h3>
                    <LeadSourceChart data={sourceChartData} />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Building2" size={16} className="text-accent" />
                      Prospects by Company Type
                    </h3>
                    <LeadSourceChart data={companyTypeData} />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6 md:col-span-2">
                    <h3 className="text-sm font-semibold text-foreground mb-4">Source Effectiveness</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 text-muted-foreground font-medium">Source</th>
                            <th className="text-right py-2 text-muted-foreground font-medium">Prospects</th>
                            <th className="text-right py-2 text-muted-foreground font-medium">Won</th>
                            <th className="text-right py-2 text-muted-foreground font-medium">Conv. Rate</th>
                            <th className="text-right py-2 text-muted-foreground font-medium">Pipeline Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {sourceChartData?.map(src => {
                            const srcKey = src?.name?.toLowerCase()?.replace(/ /g, '_');
                            const srcProspects = analyticsData?.prospects?.filter(p => p?.source === srcKey || p?.source?.replace(/_/g, ' ') === src?.name?.toLowerCase()) || [];
                            const wonCount = srcProspects?.filter(p => p?.status === 'won')?.length;
                            const convRate = srcProspects?.length > 0 ? ((wonCount / srcProspects?.length) * 100)?.toFixed(0) : 0;
                            const pipelineVal = srcProspects?.filter(p => !['won','lost']?.includes(p?.status))?.reduce((s, p) => s + (Number(p?.estimatedValue) || 0), 0);
                            return (
                              <tr key={src?.name}>
                                <td className="py-2.5 text-foreground">{src?.name}</td>
                                <td className="py-2.5 text-right text-muted-foreground">{src?.value}</td>
                                <td className="py-2.5 text-right text-green-600">{wonCount}</td>
                                <td className="py-2.5 text-right">
                                  <span className={`font-medium ${Number(convRate) >= 30 ? 'text-green-600' : 'text-muted-foreground'}`}>{convRate}%</span>
                                </td>
                                <td className="py-2.5 text-right text-foreground">${pipelineVal?.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* CONVERSION METRICS */}
              {activeTab === 'conversion' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Filter" size={16} className="text-accent" />
                      Pipeline Funnel
                    </h3>
                    <ConversionFunnelChart data={funnelData} />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4">Stage Breakdown</h3>
                    <div className="space-y-3">
                      {funnelData?.map((stage, i) => {
                        const pct = metrics?.total > 0 ? (stage?.count / metrics?.total * 100)?.toFixed(0) : 0;
                        return (
                          <div key={stage?.key}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-foreground">{stage?.name}</span>
                              <span className="text-muted-foreground">{stage?.count} ({pct}%)</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6 md:col-span-2">
                    <h3 className="text-sm font-semibold text-foreground mb-4">Conversion by Company Type</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {companyTypeData?.map(type => {
                        const typeKey = type?.name?.toLowerCase()?.replace(/ /g, '_');
                        const typeProspects = analyticsData?.prospects?.filter(p => p?.companyType === typeKey) || [];
                        const wonCount = typeProspects?.filter(p => p?.status === 'won')?.length;
                        const convRate = typeProspects?.length > 0 ? ((wonCount / typeProspects?.length) * 100)?.toFixed(0) : 0;
                        return (
                          <div key={type?.name} className="bg-muted/30 rounded-lg p-4 text-center">
                            <p className="text-xs text-muted-foreground mb-1">{type?.name}</p>
                            <p className="text-xl font-bold text-foreground">{convRate}%</p>
                            <p className="text-xs text-muted-foreground">{type?.value} prospects</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* FOLLOW-UP PERFORMANCE */}
              {activeTab === 'followup' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="grid grid-cols-2 gap-4 md:col-span-2">
                    <MetricCard label="Scheduled" value={metrics?.scheduledFollowups} icon="Clock" subValue={null} trend={null} />
                    <MetricCard label="Completed" value={metrics?.completedFollowups} icon="CheckCircle" color="text-green-600" subValue={null} trend={null} />
                    <MetricCard label="Overdue" value={metrics?.overdueFollowups} icon="AlertCircle" color="text-red-600" subValue={null} trend={null} />
                    <MetricCard label="Completion Rate" value={`${metrics?.followupCompletionRate}%`} icon="TrendingUp" color={Number(metrics?.followupCompletionRate) >= 70 ? 'text-green-600' : 'text-amber-600'} subValue={null} trend={null} />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6 md:col-span-2">
                    <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="BarChart2" size={16} className="text-accent" />
                      Follow-up Activity by Type
                    </h3>
                    <FollowupPerformanceChart data={followupChartData} />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6 md:col-span-2">
                    <h3 className="text-sm font-semibold text-foreground mb-4">Interaction Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[['call','Calls','Phone'],['email','Emails','Mail'],['meeting','Meetings','Users'],['proposal','Proposals','FileText']]?.map(([type, label, icon]) => {
                        const count = analyticsData?.interactions?.filter(i => i?.interactionType === type)?.length || 0;
                        return (
                          <div key={type} className="flex items-center gap-3 bg-muted/30 rounded-lg p-4">
                            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                              <Icon name={icon} size={16} className="text-accent" />
                            </div>
                            <div>
                              <p className="text-lg font-bold text-foreground">{count}</p>
                              <p className="text-xs text-muted-foreground">{label}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* PIPELINE FORECAST */}
              {activeTab === 'forecast' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-card border border-border rounded-lg p-6 md:col-span-2">
                    <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="TrendingUp" size={16} className="text-accent" />
                      Weighted Pipeline Value (Last 6 Months)
                    </h3>
                    <PipelineForecastChart data={forecastData} />
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4">Revenue Projections</h3>
                    <div className="space-y-4">
                      {[
                        { label: 'Total Pipeline (Active)', value: metrics?.totalPipelineValue, color: 'text-foreground' },
                        { label: 'Weighted Pipeline (Probability-Adjusted)', value: Math.round(metrics?.weightedValue), color: 'text-accent' },
                        { label: 'Won Revenue (YTD)', value: analyticsData?.prospects?.filter(p => p?.status === 'won')?.reduce((s, p) => s + (Number(p?.estimatedValue) || 0), 0) || 0, color: 'text-green-600' },
                      ]?.map(item => (
                        <div key={item?.label} className="flex justify-between items-center py-3 border-b border-border last:border-0">
                          <span className="text-sm text-muted-foreground">{item?.label}</span>
                          <span className={`text-sm font-bold ${item?.color}`}>${item?.value?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4">Top Prospects by Value</h3>
                    <div className="space-y-3">
                      {(analyticsData?.prospects || [])?.filter(p => !['won','lost']?.includes(p?.status) && p?.estimatedValue)?.sort((a, b) => (Number(b?.estimatedValue) || 0) - (Number(a?.estimatedValue) || 0))?.slice(0, 5)?.map(p => (
                        <div key={p?.id} className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{p?.companyName}</p>
                            <p className="text-xs text-muted-foreground">{p?.probability}% probability</p>
                          </div>
                          <span className="text-sm font-semibold text-foreground ml-4">${Number(p?.estimatedValue)?.toLocaleString()}</span>
                        </div>
                      ))}
                      {(analyticsData?.prospects || [])?.filter(p => !['won','lost']?.includes(p?.status) && p?.estimatedValue)?.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No active prospects with estimated values</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </MainLayout>
  );
};

export default ProspectAnalyticsDashboard;
